import type { GanttRow } from "@/shared/components/gantt";
import { Gantt } from "@/shared/components/gantt";
import { RotuloObra } from "@/shared/components/rotulo-obra";
import { TipoObraTags } from "@/shared/components/tipo-obra-tags";
import type { MarcaSimulacao } from "@/modules/recursos/simulacao";

import { obraLineLayout, obraPillLayout } from "../../gantt-obras-layout";
import type {
  ObraGroup,
  ObraRowContent,
  ObraRowPerson,
} from "@/shared/components/gantt-carga/gantt-obras-rows";
import { ConflictBadge } from "@/shared/components/conflict-badge";
import { AllocationBar } from "@/shared/components/gantt-allocation-bar";
import { ObraLineMark } from "../gantt-obra-line";
import { SIMULACAO_MARCA_CLASSES, SimulacaoMarcaTag } from "@/shared/components/simulacao-marca";
import type { GanttObrasViewModel } from "./types-gantt-obras";
import styles from "../../gantt-obras.module.scss";

/** Mobile (Figma): linha da obra com 89px e linhas de profissional com 73px. */
const MOBILE_OBRA_ROW_MIN_HEIGHT = "5.5625rem";
const MOBILE_MEMBER_ROW_MIN_HEIGHT = "4.5625rem";

interface RenderedRow {
  group: ObraGroup;
  content: ObraRowContent;
  first: boolean;
  expandable: boolean;
  /** Linhas que a obra ocupa na tela (1 recolhida; N expandida): a coluna Obra cobre todas. */
  linhas: number;
}

interface ObraIdentityProps {
  group: ObraGroup;
  expandable: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

/** Nome da obra (com o chevron, quando expande) e os tipos dela em tags abaixo. */
function ObraIdentity({ group, expandable, isOpen, onToggle }: ObraIdentityProps) {
  return (
    <>
      <div className={styles.obraTitle} title={group.rotulo.sigla}>
        {expandable ? (
          <button
            type="button"
            className={styles.toggle}
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-label={`${isOpen ? "Recolher" : "Expandir"} ${group.rotulo.nome}`}
          >
            <i className={`pi ${isOpen ? "pi-chevron-down" : "pi-chevron-right"}`} aria-hidden />
          </button>
        ) : (
          <span className={styles.toggleSpacer} aria-hidden />
        )}
        <RotuloObra {...group.rotulo} className={styles.obraName} />
      </div>
      {group.tipos.length > 0 ? <TipoObraTags tipos={group.tipos} /> : null}
    </>
  );
}

interface PersonBlockProps {
  person: ObraRowPerson;
  marca?: MarcaSimulacao;
  rotuloConflito: GanttObrasViewModel["rotuloConflito"];
}

function PersonBlock({ person, marca, rotuloConflito }: PersonBlockProps) {
  return (
    <div className={styles.person}>
      <span className={styles.personName}>
        {person.name}
        {person.conflicts > 0 ? (
          <ConflictBadge
            label={rotuloConflito(person.conflicts)}
            groups={person.conflictGroups}
            tooltip={person.conflictTooltip}
          />
        ) : null}
      </span>
      {person.team ? <span className={styles.personTeam}>{person.team}</span> : null}
      {marca ? <SimulacaoMarcaTag marca={marca} /> : null}
    </div>
  );
}

export function GanttObrasView({
  groups,
  today,
  marcas,
  rotuloConflito,
  scale,
  isMobile,
  unitMinWidth,
  colunas,
  larguraDaLinha,
  expandida,
  alternar,
  acoesDaObra,
  semAcoes,
}: GanttObrasViewModel) {
  const lineOf = (content: ObraRowContent, tall = false) =>
    content.line ? (
      <ObraLineMark
        line={content.line}
        layout={obraLineLayout(tall)}
        widthRem={larguraDaLinha(content.line.width)}
      />
    ) : null;

  const pillOf = (content: ObraRowContent) =>
    content.pill ? (
      <AllocationBar
        item={content.pill}
        layout={obraPillLayout(content.line !== null)}
        marca={marcas.get(content.marcaKey)}
        vessel={false}
      />
    ) : null;

  const linhaMarcada = (key: string) =>
    marcas.has(key) ? SIMULACAO_MARCA_CLASSES.linha : undefined;

  function desktopRows(): GanttRow[] {
    // Obra com várias pessoas resume até ser expandida; com uma só, mostra a pessoa direto.
    const rendered: RenderedRow[] = groups.flatMap((group) => {
      const expandable = group.members.length > 1;
      const content =
        group.members.length === 0 || (expandable && !expandida(group.key))
          ? [group.summary]
          : group.members;
      return content.map((item, index) => ({
        group,
        content: item,
        first: index === 0,
        expandable,
        linhas: content.length,
      }));
    });

    return rendered.map(({ group, content, first, expandable, linhas }) => ({
      key: content.key,
      testId: content.person ? "gantt-row-obra-profissional" : "gantt-row-obra",
      className: linhaMarcada(content.marcaKey),
      // O ⋮ da primeira linha é o da obra, mesmo quando ela mostra o 1º profissional.
      actionLabel: `Detalhes de ${first ? group.nome : (content.person?.name ?? group.nome)}`,
      actions: first ? acoesDaObra(group) : undefined,
      hideActions: semAcoes,
      // Figma (7824:66792): com a obra expandida a coluna Obra é uma célula só, sem linha entre os profissionais.
      cellRowSpans: first && linhas > 1 ? { 0: linhas } : undefined,
      skipCells: first ? undefined : [0],
      cells: [
        first ? (
          <div className={styles.obraDesktop}>
            <ObraIdentity
              group={group}
              expandable={expandable}
              isOpen={expandida(group.key)}
              onToggle={() => alternar(group.key)}
            />
          </div>
        ) : null,
        content.person ? (
          <PersonBlock
            person={content.person}
            marca={marcas.get(content.marcaKey)}
            rotuloConflito={rotuloConflito}
          />
        ) : (
          <span className={styles.summary}>{content.summary}</span>
        ),
      ],
      content: (
        <>
          {lineOf(content)}
          {pillOf(content)}
        </>
      ),
    }));
  }

  function mobileRows(): GanttRow[] {
    return groups.flatMap((group) => {
      // No mobile toda obra com equipe expande, inclusive a de uma pessoa só.
      const expandable = group.members.length > 0;
      const isOpen = expandable && expandida(group.key);

      const obraRow: GanttRow = {
        key: `${group.key}-obra`,
        testId: "gantt-row-obra",
        minHeight: MOBILE_OBRA_ROW_MIN_HEIGHT,
        actionLabel: `Detalhes de ${group.rotulo.nome}`,
        actions: acoesDaObra(group),
        hideActions: semAcoes,
        cells: [
          <div key="obra" className={styles.mobileObra}>
            <ObraIdentity
              group={group}
              expandable={expandable}
              isOpen={isOpen}
              onToggle={() => alternar(group.key)}
            />
            {isOpen ? null : <span className={styles.mobileSummary}>{group.summary.summary}</span>}
          </div>,
        ],
        content: lineOf(group.summary, true),
      };

      const memberRows: GanttRow[] = isOpen
        ? group.members.map((member) => ({
            key: member.key,
            testId: "gantt-row-obra-profissional",
            minHeight: MOBILE_MEMBER_ROW_MIN_HEIGHT,
            className: linhaMarcada(member.marcaKey),
            actionLabel: `Detalhes de ${member.person?.name ?? group.rotulo.nome}`,
            hideActions: semAcoes,
            cells: [
              member.person ? (
                <div key="person" className={styles.mobilePerson}>
                  <PersonBlock
                    person={member.person}
                    marca={marcas.get(member.marcaKey)}
                    rotuloConflito={rotuloConflito}
                  />
                </div>
              ) : null,
            ],
            content: (
              <>
                {lineOf(member)}
                {pillOf(member)}
              </>
            ),
          }))
        : [];

      return [obraRow, ...memberRows];
    });
  }

  return (
    <Gantt.Root
      grid="none"
      className={styles.root}
      corpoRola
      scale={scale}
      today={today}
      columns={colunas}
      rows={isMobile ? mobileRows() : desktopRows()}
      withActions
      unitMinWidth={unitMinWidth}
    />
  );
}
